import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/ui/PageHeader';
import type { CategoriaServicioRow, EstadoGeneral } from '../../types/database.types';

interface FormState {
  categoria_id: string;
  codigo: string;
  nombre: string;
  descripcion: string;
  duracion_minutos: string;
  precio: string;
  estado: EstadoGeneral;
  disponible_online: boolean;
}

const VACIO: FormState = {
  categoria_id: '',
  codigo: '',
  nombre: '',
  descripcion: '',
  duracion_minutos: '30',
  precio: '0',
  estado: 'activo',
  disponible_online: true,
};

/** Alta y edición de servicios (DAF v2, sección 7). */
export function ServicioForm() {
  const { id } = useParams<{ id: string }>();
  const esNuevo = id === 'nuevo';
  const navigate = useNavigate();
  const { perfil } = useAuth();

  const [categorias, setCategorias] = useState<CategoriaServicioRow[]>([]);
  const [form, setForm] = useState<FormState>(VACIO);
  const [loading, setLoading] = useState(!esNuevo);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('categorias_servicios')
      .select('*')
      .eq('estado', 'activo')
      .order('nombre')
      .then(({ data }) => data && setCategorias(data));
  }, []);

  useEffect(() => {
    if (esNuevo || !id) return;

    supabase
      .from('servicios')
      .select('*')
      .eq('id', id)
      .single()
      .then(({ data, error: err }) => {
        if (err || !data) {
          setError(err?.message ?? 'No se encontró el servicio');
        } else {
          setForm({
            categoria_id: data.categoria_id,
            codigo: data.codigo,
            nombre: data.nombre,
            descripcion: data.descripcion ?? '',
            duracion_minutos: String(data.duracion_minutos),
            precio: String(data.precio),
            estado: data.estado,
            disponible_online: data.disponible_online,
          });
        }
        setLoading(false);
      });
  }, [esNuevo, id]);

  function actualizar<K extends keyof FormState>(campo: K, valor: FormState[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    // useParams() tipa `id` como `string | undefined` aunque la ruta
    // siempre lo trae ('nuevo' o un uuid real) -- este guard se lo
    // confirma a TypeScript para que `.eq('id', id)` más abajo compile
    // con `id: string` en vez de `string | undefined`.
    if (!id) return;
    setGuardando(true);
    setError(null);

    const payload = {
      salon_id: perfil.salon_id,
      categoria_id: form.categoria_id,
      codigo: form.codigo,
      nombre: form.nombre,
      descripcion: form.descripcion || null,
      duracion_minutos: Number(form.duracion_minutos),
      precio: Number(form.precio),
      estado: form.estado,
      disponible_online: form.disponible_online,
    };

    const { error: err } = esNuevo
      ? await supabase.from('servicios').insert(payload)
      : await supabase.from('servicios').update(payload).eq('id', id);

    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    navigate('/admin/servicios');
  }

  if (loading) return <p className="text-sm text-onyx/50">Cargando…</p>;

  return (
    <div className="max-w-2xl">
      <PageHeader title={esNuevo ? 'Nuevo servicio' : 'Editar servicio'} />

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Nombre" required>
            <input
              required
              className="input-field"
              value={form.nombre}
              onChange={(e) => actualizar('nombre', e.target.value)}
            />
          </Campo>
          <Campo label="Código" required>
            <input
              required
              className="input-field"
              value={form.codigo}
              onChange={(e) => actualizar('codigo', e.target.value)}
            />
          </Campo>
        </div>

        <Campo label="Categoría" required>
          <select
            required
            className="input-field"
            value={form.categoria_id}
            onChange={(e) => actualizar('categoria_id', e.target.value)}
          >
            <option value="" disabled>
              Selecciona una categoría
            </option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </Campo>

        <Campo label="Descripción">
          <textarea
            className="input-field"
            rows={2}
            value={form.descripcion}
            onChange={(e) => actualizar('descripcion', e.target.value)}
          />
        </Campo>

        <div className="grid grid-cols-2 gap-4">
          <Campo label="Duración (minutos)" required>
            <input
              required
              type="number"
              min={1}
              className="input-field"
              value={form.duracion_minutos}
              onChange={(e) => actualizar('duracion_minutos', e.target.value)}
            />
          </Campo>
          <Campo label="Precio (COP)" required>
            <input
              required
              type="number"
              min={0}
              step={1000}
              className="input-field"
              value={form.precio}
              onChange={(e) => actualizar('precio', e.target.value)}
            />
          </Campo>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Campo label="Estado">
            <select
              className="input-field"
              value={form.estado}
              onChange={(e) => actualizar('estado', e.target.value as EstadoGeneral)}
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </Campo>
          <label className="flex items-center gap-2 pt-6 text-sm text-onyx/80">
            <input
              type="checkbox"
              checked={form.disponible_online}
              onChange={(e) => actualizar('disponible_online', e.target.checked)}
            />
            Disponible para reserva online
          </label>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/servicios')}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function Campo({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-onyx/80">
        {label}
        {required && <span className="text-gold-deep"> *</span>}
      </span>
      {children}
    </label>
  );
}
